// FILE: /src/Components/PostUploader/index.js (FULLY UPDATED)

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Button, DatePicker, Select, Dropdown, Input, Divider, TimePicker, Spin, Modal, message, Tooltip, Checkbox, Switch } from "antd";
import { useSelector } from "react-redux";
import { uploadSingleImage } from "../../services/user.profile";
import moment from "moment";
import { getBoardApi, createBoardApi, createPlaylistApi } from "../../services/publish";
import UpgradeModal from '../BuySubscription/UpgradeModal';
import axiosServices from "../../services/axios";
import Images from "../../Comman Components/Images";
import PinImage from "../../Comman Components/Pinterest Componets/PinImage";
import PinName from "../../Comman Components/Pinterest Componets/PinName";

const { TextArea } = Input;
const { Option } = Select;

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MIN_VIDEO_DURATION_S = 3;
const MAX_VIDEO_DURATION_S = 900;
const MIN_ASPECT_RATIO = 0.01;
const MAX_ASPECT_RATIO = 10.0;
const REQUIRED_ASPECT_RATIO = 1.0;
const ASPECT_RATIO_TOLERANCE = 0.02;

const getMediaDimensions = (file) => {
    return new Promise((resolve, reject) => {
        const isVideo = file.type.startsWith('video/');
        const url = URL.createObjectURL(file);

        if (isVideo) {
            const video = document.createElement('video');
            video.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                resolve({ width: video.videoWidth, height: video.videoHeight });
            };
            video.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error(`Could not read video metadata for ${file.name}.`));
            };
            video.src = url;
        } else {
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(url);
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error(`Could not read image metadata for ${file.name}.`));
            };
            img.src = url;
        }
    });
};

const validateSingleVideoFile = (file) => {
    return new Promise((resolve) => {
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';
        videoElement.src = URL.createObjectURL(file);
        
        videoElement.onloadedmetadata = () => {
            URL.revokeObjectURL(videoElement.src);
            const { duration, videoWidth, videoHeight } = videoElement;
            const aspectRatio = videoWidth / videoHeight;
            
            const metadata = { duration, width: videoWidth, height: videoHeight };

            if (duration < MIN_VIDEO_DURATION_S || duration > MAX_VIDEO_DURATION_S) {
                resolve({ isValid: false, message: `Video "${file.name}" must be between ${MIN_VIDEO_DURATION_S}s and ${MAX_VIDEO_DURATION_S / 60}m.`, metadata });
            } else if (aspectRatio < MIN_ASPECT_RATIO || aspectRatio > MAX_ASPECT_RATIO) {
                resolve({ isValid: false, message: `Video "${file.name}" has an unsupported aspect ratio.`, metadata });
            } else {
                resolve({ isValid: true, metadata });
            }
        };
        videoElement.onerror = () => {
            URL.revokeObjectURL(videoElement.src);
            resolve({ isValid: false, message: `Could not read metadata for "${file.name}". It may be corrupt.`, metadata: null });
        };
    });
};

const PlatformOption = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="p-4 border rounded-lg bg-gray-50">
            <button className="w-full text-left font-semibold text-gray-700 mb-3 flex justify-between items-center" onClick={() => setIsOpen(!isOpen)}>
                {title}
                <i className={`fa-solid fa-chevron-down transform transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isOpen && children}
        </div>
    );
};

const TIKTOK_PRIVACY_LABELS = {
PUBLIC_TO_EVERYONE: "Everyone",
MUTUAL_FOLLOW_FRIENDS: "Friends",
FOLLOWER_OF_CREATOR: "Followers",
SELF_ONLY: "Only Me",
};

const hasReachedTikTokDailyLimit = (creatorInfo) => {
const remaining = creatorInfo?.remaining_daily_post_count;

if (remaining === null || remaining === undefined || remaining === '') {
return false;
}

return Number(remaining) === 0;
};

const normalizeTikTokPrivacyOptions = (creatorInfo) => {
const apiOptions = creatorInfo?.privacy_level_options;
if (Array.isArray(apiOptions) && apiOptions.length > 0) {
return apiOptions;
}

return ["PUBLIC_TO_EVERYONE", "MUTUAL_FOLLOW_FRIENDS", "SELF_ONLY"];
};

const TikTokOptions = ({
options,
setOptions,
creatorInfo = null,
creatorInfoLoading = false,
creatorInfoError = null,
isPhotoPost = false,
isVideoPost = false,
selectedTikTokProfile = null,
}) => {
const commercialToggleOn = options.brand_content_toggle === true;
const brandedContentOn =
options.branded_content_toggle === true ||
options.branded_content_type === "BRANDED_CONTENT";

const privacyOptions = normalizeTikTokPrivacyOptions(creatorInfo);

const handleOptionChange = (key, value) => {
setOptions((prev) => ({ ...prev, [key]: value }));
};

const handleCommercialToggle = (checked) => {
setOptions((prev) => ({
...prev,
brand_content_toggle: checked,
brand_organic_toggle: checked ? prev.brand_organic_toggle || false : false,
branded_content_toggle: checked ? prev.branded_content_toggle || false : false,
branded_content_type: undefined,
privacy_level:
checked && prev.branded_content_toggle === true && prev.privacy_level === "SELF_ONLY"
? undefined
: prev.privacy_level,
}));
};

const handleBrandOrganicChange = (checked) => {
setOptions((prev) => ({
...prev,
brand_organic_toggle: checked,
branded_content_type:
checked && !prev.branded_content_toggle ? "BRAND_ORGANIC" : prev.branded_content_type,
}));
};

const handleBrandedContentChange = (checked) => {
setOptions((prev) => ({
...prev,
branded_content_toggle: checked,
branded_content_type:
checked
? "BRANDED_CONTENT"
: prev.brand_organic_toggle
? "BRAND_ORGANIC"
: undefined,
privacy_level: checked && prev.privacy_level === "SELF_ONLY" ? undefined : prev.privacy_level,
}));
};

useEffect(() => {
if (brandedContentOn && options.privacy_level === "SELF_ONLY") {
setOptions((prev) => ({ ...prev, privacy_level: undefined }));
}
}, [brandedContentOn, options.privacy_level, setOptions]);

const creatorCommentDisabled = Boolean(creatorInfo?.comment_disabled);
const creatorDuetDisabled = Boolean(creatorInfo?.duet_disabled);
const creatorStitchDisabled = Boolean(creatorInfo?.stitch_disabled);

return (
<PlatformOption title="TikTok Options">
<div className="space-y-4">
{selectedTikTokProfile && (
<div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
<Images
Id={selectedTikTokProfile.platform_id}
fallbackName={selectedTikTokProfile.profile}
className="w-9 h-9 rounded-full object-cover"
/>
<div>
<p className="text-xs text-gray-500 mb-0">Posting to TikTok as</p>
<p className="text-sm font-semibold text-gray-800 mb-0">
{selectedTikTokProfile.profile || creatorInfo?.creator_nickname || creatorInfo?.display_name || "TikTok Creator"}
</p>
</div>
</div>
)}

{creatorInfoLoading && (
<div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-sm text-blue-700">
Loading TikTok creator settings...
</div>
)}

{creatorInfoError && (
<div className="rounded-md bg-red-50 border border-red-100 p-3 text-sm text-red-700">
{creatorInfoError}
</div>
)}

{creatorInfo?.can_post === false && (
<div className="rounded-md bg-red-50 border border-red-100 p-3 text-sm text-red-700">
You cannot make more posts at this moment. Please try again later.
</div>
)}

{hasReachedTikTokDailyLimit(creatorInfo) && (
<div className="rounded-md bg-red-50 border border-red-100 p-3 text-sm text-red-700">
This TikTok account has reached its daily posting limit. Please try again after TikTok resets the limit.
</div>
)}

<div>
<label className="block text-sm font-medium text-gray-700 mb-1">
Who can view this post <span className="text-red-500">*</span>
</label>
<Select
placeholder="Select privacy level"
value={options.privacy_level}
onChange={(value) => handleOptionChange("privacy_level", value)}
className="w-full"
loading={creatorInfoLoading}
>
{privacyOptions.map((privacyValue) => {
const isPrivateBlockedForBrandedContent =
brandedContentOn && privacyValue === "SELF_ONLY";

const label = TIKTOK_PRIVACY_LABELS[privacyValue] || privacyValue;

return (
<Select.Option
key={privacyValue}
value={privacyValue}
disabled={isPrivateBlockedForBrandedContent}
title={
isPrivateBlockedForBrandedContent
? "Branded content visibility cannot be set to private."
: undefined
}
>
{isPrivateBlockedForBrandedContent ? (
<Tooltip title="Branded content visibility cannot be set to private.">
<span>{label}</span>
</Tooltip>
) : (
label
)}
</Select.Option>
);
})}
</Select>
</div>

<div>
<label className="block text-sm font-medium text-gray-700 mb-1">Interactions</label>
<div className="space-y-2">
<Checkbox
checked={options.comment_disabled === false}
disabled={creatorCommentDisabled}
onChange={(e) => handleOptionChange("comment_disabled", !e.target.checked)}
>
Allow Comments
</Checkbox>

{isPhotoPost ? (
<div className="text-xs text-gray-500">
Duet and Stitch are not available for TikTok photo posts.
</div>
) : (
<>
<Checkbox
checked={options.duet_disabled === false}
disabled={creatorDuetDisabled || !isVideoPost}
onChange={(e) => handleOptionChange("duet_disabled", !e.target.checked)}
>
Allow Duet
</Checkbox>
<Checkbox
checked={options.stitch_disabled === false}
disabled={creatorStitchDisabled || !isVideoPost}
onChange={(e) => handleOptionChange("stitch_disabled", !e.target.checked)}
>
Allow Stitch
</Checkbox>
</>
)}
</div>
</div>

<div>
<label className="block text-sm font-medium text-gray-700 mb-1">Commercial Content Disclosure</label>
<div className="flex items-center gap-2">
<Switch
checked={commercialToggleOn}
onChange={handleCommercialToggle}
/>
<span>Disclose commercial content</span>
</div>

{commercialToggleOn && (
<div className="mt-3 pl-6 space-y-3 animate-fade-in-up">
<div className="flex flex-col gap-2">
<Checkbox
checked={options.brand_organic_toggle === true}
onChange={(e) => handleBrandOrganicChange(e.target.checked)}
>
Your brand
</Checkbox>

<Checkbox
checked={options.branded_content_toggle === true}
onChange={(e) => handleBrandedContentChange(e.target.checked)}
>
Branded content
</Checkbox>
</div>

<p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2">
By enabling commercial content disclosure, you confirm this post follows TikTok’s branded content and commercial music policies, and that you have the necessary rights to use any music/audio included.
</p>
</div>
)}
</div>
</div>
</PlatformOption>
);
};


export default function PostUploader({
    messages, setMessage, photoPreview, setPhotoPreview,
    instagramOptions, setInstagramOptions, pinterestOptions, setPinterestOptions,
    linkedinOptions, setLinkedinOptions,
    youtubeOptions, setYoutubeOptions,
tiktokOptions, setTiktokOptions,
tiktokCreatorInfo, tiktokCreatorInfoLoading, tiktokCreatorInfoError,
scheduleDates, setScheduleDates, availableProfiles, selectedProfiles,
    scheduleType, setScheduleType, onFileUploadError,
    brandTimezone,
    setIsCaptionModalOpen, setIsImageModalOpen, setIsSocialPostModalOpen
    }) {
    const uploadFileRef = useRef(null);
    const { selectedGroup, groups } = useSelector((state) => state.groups);
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });

    const isLinkedInSelected = useMemo(
        () => selectedProfiles.some(
            p => p.platform === 'LINKEDIN' || p.platform === 'LINKEDIN_COMPANY'
        ),
        [selectedProfiles]
        );
    const isTikTokSelected = useMemo(() => selectedProfiles.some(p => p.platform === 'TIKTOK'), [selectedProfiles]);
    const isDocumentPost = useMemo(() => photoPreview.length === 1 && photoPreview[0].type === 'document', [photoPreview]);
    const isArticlePost = useMemo(() => photoPreview.length === 0 && isLinkedInSelected && linkedinOptions.articleUrl, [photoPreview, isLinkedInSelected, linkedinOptions.articleUrl]);

    const handleUploadClick = () => uploadFileRef.current.click();

    const handleFileUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
    
        const groupId = groups.find(g => g.name === selectedGroup)?._id;
        if (!groupId) {
            onFileUploadError("Cannot upload: Active group is not selected or invalid.");
            return;
        }
    
        const isMultiUpload = files.length > 1;
        const isInstagramSelected = selectedProfiles.some(p => p.platform === 'INSTAGRAM');
    
        for (const file of Array.from(files)) {
            let isFileValid = true;
            let videoMetadata = null; 
    
            if (file.size > MAX_FILE_SIZE_BYTES) {
                onFileUploadError(`${file.name} is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
                isFileValid = false;
            }
            const isVideo = file.type.startsWith('video/');
            const isImage = file.type.startsWith('image/');
            const isDoc = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'].includes(file.type);
            
            if (!isImage && !isVideo && !isDoc) {
                onFileUploadError(`${file.name} has an unsupported file type.`);
                isFileValid = false;
            }
    
            if (isFileValid && isMultiUpload && isInstagramSelected) {
                if (isVideo && file.type !== 'video/mp4') {
                    onFileUploadError(`Carousel videos for Instagram must be MP4. Skipped "${file.name}".`);
                    isFileValid = false;
                }
                if (isDoc) {
                    onFileUploadError(`Documents cannot be part of a multi-media post. Skipped "${file.name}".`);
                    isFileValid = false;
                }
                if (isFileValid) {
                    try {
                        const { width, height } = await getMediaDimensions(file);
                        const aspectRatio = width / height;
                        if (Math.abs(aspectRatio - REQUIRED_ASPECT_RATIO) > ASPECT_RATIO_TOLERANCE) {
                             onFileUploadError(`Instagram carousels require a 1:1 aspect ratio. Skipped "${file.name}".`);
                             isFileValid = false;
                        }
                    } catch (error) {
                        onFileUploadError(error.message);
                        isFileValid = false;
                    }
                }
            } else if (isFileValid && isMultiUpload && isDoc) {
                onFileUploadError(`Documents cannot be part of a multi-media post. Skipped "${file.name}".`);
                isFileValid = false;
            }
            
            if (isFileValid && isVideo && !isMultiUpload) {
                 const validationResult = await validateSingleVideoFile(file);
                 videoMetadata = validationResult.metadata; 
                 if (!validationResult.isValid) {
                     onFileUploadError(validationResult.message);
                     isFileValid = false;
                 }
            }
            
            if (isFileValid) {
                initiateUpload(file, groupId, videoMetadata); 
            }
        }
    };

    const initiateUpload = (file, groupId, videoMetadata = null) => {
        const tempId = `temp_${Date.now()}_${file.name}`;
        const localPreviewUrl = URL.createObjectURL(file);
        const isVideo = file.type.startsWith('video/');
        const isDocument = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'].includes(file.type);

        let fileType = 'image';
        if (isVideo) fileType = 'video';
        if (isDocument) fileType = 'document';

        const newItem = {
            id: tempId,
            localUrl: localPreviewUrl,
            permanentUrl: null,
            type: fileType,
            file: file,
            status: 'uploading',
            metadata: videoMetadata,
        };
        setPhotoPreview(prev => [...prev, newItem]);

        uploadSingleImage(file, groupId)
            .then(response => {
                setTimeout(() => {
                    setPhotoPreview(prev => prev.map(item =>
                        item.id === tempId
                            ? { ...item, permanentUrl: response.url, status: 'completed', file: null }
                            : item
                    ));
                }, 1000);
            })
            .catch(error => {
                if (error.isLimitError) {
                    setUpgradeModal({ visible: true, message: error.message, featureName: error.feature });
                    setPhotoPreview(prev => prev.filter(item => item.id !== tempId));
                } else {
                    console.error("Upload failed for file:", file.name, error);
                    onFileUploadError(`Upload failed for ${file.name}.`);
                    setPhotoPreview(prev => prev.map(item =>
                        item.id === tempId
                            ? { ...item, status: 'failed' }
                            : item
                    ));
                }
            });
    };
    
    const handleRemoveMedia = (idToRemove) => {
        const itemToRemove = photoPreview.find(item => item.id === idToRemove);
        if (itemToRemove && itemToRemove.localUrl.startsWith('blob:')) {
            URL.revokeObjectURL(itemToRemove.localUrl);
        }
        setPhotoPreview(prev => prev.filter(item => item.id !== idToRemove));
    };

    const aiToolsMenu = useMemo(() => ({
        items: [
            { key: '1', label: 'Generate Caption from Media', onClick: () => setIsCaptionModalOpen(true), disabled: photoPreview.length === 0 },
            { key: '2', label: 'Generate New Image', onClick: () => setIsImageModalOpen(true) },
            { key: '3', label: 'Generate Social Media Post', onClick: () => setIsSocialPostModalOpen(true) },
        ]
    }), [photoPreview.length, setIsCaptionModalOpen, setIsImageModalOpen, setIsSocialPostModalOpen]);
    
    const youtubeProfiles = useMemo(
        () => selectedProfiles.filter(p => p.platform === 'YOUTUBE'),
        [selectedProfiles]
        );

        const isTikTokPhotoPost = useMemo(
        () => photoPreview.length > 0 && photoPreview.every(item => item.type === 'image'),
        [photoPreview]
        );

        const isTikTokVideoPost = useMemo(
        () => photoPreview.length === 1 && photoPreview[0]?.type === 'video',
        [photoPreview]
        );

        const selectedTikTokProfile = useMemo(
        () => selectedProfiles.find(p => p.platform === 'TIKTOK') || null,
        [selectedProfiles]
        );

        return (
        <div className="space-y-5">
            <div className="border border-dashed border-gray-300 rounded-lg p-4 min-h-[160px]">
                {photoPreview.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {photoPreview.map((item) => (
                            <div key={item.id} className="relative aspect-square">
                                {item.type === 'video' ? (
                                    <video src={item.localUrl} className="w-full h-full object-cover rounded-md bg-black" />
                                ) : item.type === 'document' ? (
                                    <div className="w-full h-full bg-gray-200 rounded-md flex flex-col items-center justify-center text-center p-2">
                                        <i className="fa-solid fa-file-alt text-4xl text-gray-500"></i>
                                        <p className="text-xs text-gray-600 mt-2 truncate">
                                            {item.file ? item.file.name : (item.permanentUrl?.split('/').pop().split('-').pop() || 'Document')}
                                        </p>
                                    </div>
                                ) : (
                                    <img src={item.localUrl} alt={`preview ${item.id}`} className="w-full h-full object-cover rounded-md" />
                                )}

                                {item.status === 'uploading' && (
                                    <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded-md">
                                        <Spin tip="Uploading..." size="small" />
                                    </div>
                                )}
                                {item.status === 'failed' && (
                                    <div className="absolute inset-0 bg-red-800 bg-opacity-70 flex items-center justify-center rounded-md text-white text-center text-xs p-1">
                                        Upload Failed
                                    </div>
                                )}

                                <button onClick={() => handleRemoveMedia(item.id)} className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-500 transition">
                                    <i className="fa-solid fa-times"></i>
                                </button>
                            </div>
                        ))}
                        {photoPreview.length < 10 && (
                            <button onClick={handleUploadClick} className="flex items-center justify-center aspect-square border-2 border-dashed rounded-md text-gray-400 hover:border-blue-500 hover:text-blue-500 transition">
                                <i className="fa-solid fa-plus text-2xl"></i>
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-10 cursor-pointer" onClick={handleUploadClick}>
                        <i className="fa-solid fa-cloud-arrow-up text-4xl text-gray-300"></i>
                        <p className="mt-2 text-sm font-semibold text-gray-600">Drag & drop or click to upload media</p>
                        <p className="mt-2 text-xs text-gray-500">
                            Images, Videos, and Documents (PDF, DOCX, PPTX) supported.
                            <br />
                            Max size: 100MB. Video: 3s - 15min. Multi-uploads must be 1:1 ratio.
                        </p>
                    </div>
                )}
                <input
                    type="file"
                    ref={uploadFileRef}
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,application/pdf,.docx,.pptx"
                />
            </div>

            <div>
                <TextArea
                    rows={6}
                    value={messages}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write something or generate a caption with AI..."
                    className="w-full"
                />
                <div className="flex justify-between items-center mt-2">
                    <Dropdown menu={aiToolsMenu} trigger={['click']}>
                        <Button>
                            <i className="fa-solid fa-wand-magic-sparkles mr-2 text-yellow-500"></i>
                            Generate with AI
                        </Button>
                    </Dropdown>
                    <span className="text-xs text-gray-400">{messages.length} / 5000</span>
                </div>
            </div>
            
            {selectedProfiles.some(p => p.platform === 'INSTAGRAM') && (
                <PlatformOption title="Instagram Options">
                    <Input placeholder="Add a first comment..." value={instagramOptions.firstComment} onChange={e => setInstagramOptions({ ...instagramOptions, firstComment: e.target.value })} />
                </PlatformOption>
            )}
            
            {selectedProfiles.filter(p => p.platform === 'PINTEREST').map(profile => (
                <PinterestOptions
                    key={profile.platform_id}
                    profile={profile}
                    availableProfiles={availableProfiles}
                    pinterestOptions={pinterestOptions}
                    setPinterestOptions={setPinterestOptions}
                    photoPreview={photoPreview}
                />
            ))}
            
            {isLinkedInSelected && (
                <PlatformOption title="LinkedIn Options">
                    <div className="space-y-3">
                    <Tooltip title="If you add a URL here, your post will become an article share. This is disabled if you have uploaded media.">
                        <Input
                        placeholder="Article URL to share"
                        value={linkedinOptions.articleUrl || ''}
                        onChange={e => setLinkedinOptions(prev => ({ ...prev, articleUrl: e.target.value }))}
                        disabled={photoPreview.length > 0}
                        />
                    </Tooltip>

                    {isArticlePost && (
                        <Input
                        placeholder="Article Title (Required)"
                        value={linkedinOptions.articleTitle || ''}
                        onChange={e => setLinkedinOptions(prev => ({ ...prev, articleTitle: e.target.value }))}
                        />
                    )}

                    {isDocumentPost && (
                        <Input
                        placeholder="Document Title (Required)"
                        value={linkedinOptions.documentTitle || ''}
                        onChange={e => setLinkedinOptions(prev => ({ ...prev, documentTitle: e.target.value }))}
                        />
                    )}

                    <p className="text-xs text-gray-500">
                        These LinkedIn options apply to both Personal Profiles and Company Pages.
                    </p>
                    </div>
                </PlatformOption>
                )}
            
            {youtubeProfiles.length > 0 && (
                <div className="space-y-4">
                    {youtubeProfiles.map(ytProfile => (
                        <YouTubeOptions
                            key={ytProfile.platform_id}
                            youtubeProfile={ytProfile}
                            youtubeOptions={youtubeOptions}
                            setYoutubeOptions={setYoutubeOptions}
                        />
                    ))}
                </div>
            )}

            {isTikTokSelected && (
                <TikTokOptions
                options={tiktokOptions}
                setOptions={setTiktokOptions}
                creatorInfo={tiktokCreatorInfo}
                creatorInfoLoading={tiktokCreatorInfoLoading}
                creatorInfoError={tiktokCreatorInfoError}
                isPhotoPost={isTikTokPhotoPost}
                isVideoPost={isTikTokVideoPost}
                selectedTikTokProfile={selectedTikTokProfile}
                />
                )}

            <PlatformOption title="Scheduling Options">
                <ScheduleManager 
                    scheduleType={scheduleType} 
                    setScheduleType={setScheduleType} 
                    scheduleDates={scheduleDates} 
                    setScheduleDates={setScheduleDates}
                    brandTimezone={brandTimezone}
                />
            </PlatformOption>

            <UpgradeModal
                isOpen={upgradeModal.visible}
                onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
                message={upgradeModal.message}
                featureName={upgradeModal.featureName}
            />
        </div>
    );
}

const PinterestOptions = ({ profile, availableProfiles, pinterestOptions, setPinterestOptions, photoPreview }) => {
    const [boards, setBoards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateBoardModalVisible, setCreateBoardModalVisible] = useState(false);
    const [newBoardName, setNewBoardName] = useState('');
    const [newBoardDesc, setNewBoardDesc] = useState('');
    const [isCreatingBoard, setIsCreatingBoard] = useState(false);
    const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);

    const thumbnailRef = useRef(null);
    const { selectedGroup, groups } = useSelector((state) => state.groups);
    const isVideoPost = useMemo(() => photoPreview.length === 1 && photoPreview[0].type === 'video', [photoPreview]);

    const accountOptions = pinterestOptions[profile.platform_id] || {};

    const handleOptionChange = (key, value) => {
        setPinterestOptions(prev => ({
            ...prev,
            [profile.platform_id]: {
                ...(prev[profile.platform_id] || {}),
                [key]: value
            }
        }));
    };

    const fetchBoards = useCallback(async () => {
        if (profile) {
            setIsLoading(true);
            try {
                const res = await getBoardApi(profile.platform_id);
                setBoards(res.data || []);
                if (res.data?.[0] && !accountOptions.board) {
                    handleOptionChange('board', res.data[0]);
                }
            } catch (error) {
                message.error("Could not fetch Pinterest boards.");
            } finally {
                setIsLoading(false);
            }
        }
    }, [profile, accountOptions.board]);
    
    useEffect(() => {
        fetchBoards();
    }, [fetchBoards]);

    const handleCreateBoard = async () => {
        setIsCreatingBoard(true);
        try {
            const res = await createBoardApi({
                DefaultBoard: profile.platform_id,
                newBoardName,
                newBoardDes: newBoardDesc
            });
            if (!res.error) {
                message.success(`Board "${res.data.name}" created!`);
                const newBoard = res.data;
                setBoards(prev => [newBoard, ...prev]);
                handleOptionChange('board', newBoard);
                setCreateBoardModalVisible(false);
                setNewBoardName('');
                setNewBoardDesc('');
            } else {
                message.error(res.message);
            }
        } catch (err) {
            message.error("Failed to create board.");
        } finally {
            setIsCreatingBoard(false);
        }
    };

    const handleThumbnailUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !file.type.startsWith('image/')) {
            message.error("Please select a valid image file for the thumbnail.");
            return;
        }

        const groupId = groups.find(g => g.name === selectedGroup)?._id;
        setIsThumbnailUploading(true);
        try {
            const response = await uploadSingleImage(file, groupId);
            handleOptionChange('thumbnailUrl', response.url);
            message.success("Thumbnail uploaded.");
        } catch (error) {
            message.error("Thumbnail upload failed.");
        } finally {
            setIsThumbnailUploading(false);
        }
    };

    return (
        <>
            <PlatformOption title={
                <div className="flex items-center gap-2">
                    <PinImage Id={profile.platform_id} />
                    <PinName Id={profile.platform_id} />
                </div>
            }>
                <div className="space-y-3">
                    <Input 
                        placeholder="Pin Title (Required)" 
                        value={accountOptions.title || ''} 
                        onChange={e => handleOptionChange('title', e.target.value)} 
                    />
                    <p className="text-xs text-gray-500 mt-1">The main caption box will be used as this Pin's description.</p>
                    <Input 
                        placeholder="Destination Link (optional)" 
                        value={accountOptions.link || ''} 
                        onChange={e => handleOptionChange('link', e.target.value)} 
                    />
                    <Select
                        placeholder="Select a board (Required)"
                        loading={isLoading}
                        value={accountOptions.board?.id}
                        onChange={(value) => handleOptionChange('board', boards.find(b => b.id === value))}
                        className="w-full"
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <Button type="link" onClick={() => setCreateBoardModalVisible(true)} block>
                                    <i className="fa-solid fa-plus mr-2"></i> Create New Board
                                </Button>
                            </>
                        )}
                    >
                        {boards && boards.filter(board => board && board.name).map(board => (
                            <Option key={board.id} value={board.id}>{board.name}</Option>
                        ))}
                    </Select>

                    {isVideoPost && (
                        <div className="pt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Video Thumbnail (Required)</label>
                            {accountOptions.thumbnailUrl ? (
                                <div className="relative w-24 h-24">
                                    <img src={accountOptions.thumbnailUrl} alt="Thumbnail preview" className="w-full h-full object-cover rounded-md" />
                                    <button onClick={() => handleOptionChange('thumbnailUrl', null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition">
                                        <i className="fa-solid fa-times"></i>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input type="file" ref={thumbnailRef} onChange={handleThumbnailUpload} className="hidden" accept="image/*" />
                                    <Button onClick={() => thumbnailRef.current.click()} loading={isThumbnailUploading}>
                                        Upload Thumbnail
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </PlatformOption>

            <Modal
                title="Create a New Pinterest Board"
                open={isCreateBoardModalVisible}
                onCancel={() => setCreateBoardModalVisible(false)}
                onOk={handleCreateBoard}
                confirmLoading={isCreatingBoard}
                okText="Create Board"
            >
                <div className="space-y-4 py-4">
                    <Input placeholder="Board Name" value={newBoardName} onChange={e => setNewBoardName(e.target.value)} />
                    <TextArea rows={3} placeholder="Description (optional)" value={newBoardDesc} onChange={e => setNewBoardDesc(e.target.value)} />
                </div>
            </Modal>
        </>
    );
};

const ScheduleManager = ({ scheduleType, setScheduleType, scheduleDates, setScheduleDates, brandTimezone }) => {
    const addSchedule = () => setScheduleDates([...scheduleDates, { date: null, time: null }]);
    const removeSchedule = (index) => setScheduleDates(scheduleDates.filter((_, i) => i !== index));
    
    const updateSchedule = (index, field, value) => {
        const newDates = [...scheduleDates];
        newDates[index][field] = value;
        setScheduleDates(newDates);
    };

    const duplicateSchedule = (index) => {
        const itemToDuplicate = scheduleDates[index];
        const newSchedule = {
            date: itemToDuplicate.date ? itemToDuplicate.date.clone() : null,
            time: itemToDuplicate.time ? itemToDuplicate.time.clone() : null,
        };
        setScheduleDates(prev => [...prev, newSchedule]);
    };

    const setPublishNow = () => {
        const now = moment();
        setScheduleType('now');
        setScheduleDates([{ date: now, time: now }]);
    };

    const userTimezone = moment.tz.guess();
    
    const renderTimezoneInfo = (item) => {
        if (!item.date || !item.time || !brandTimezone || brandTimezone === userTimezone) {
            return null;
        }
        
        const combinedDateTime = moment(item.date).set({
            hour: item.time.hour(),
            minute: item.time.minute()
        });

        const brandTime = combinedDateTime.clone().tz(brandTimezone).format('h:mm A');
        const userTime = combinedDateTime.format('h:mm A');

        return (
            <p className="text-xs text-gray-500 mt-1 pl-1">
                This is {userTime} in your timezone ({userTimezone.replace(/_/g, ' ')}), and {brandTime} in the brand's timezone ({brandTimezone.replace(/_/g, ' ')}).
            </p>
        );
    };
    
    return (
        <div className="space-y-3">
            <Select value={scheduleType} onChange={setScheduleType} className="w-full">
                <Option value="now">Publish Now</Option>
                <Option value="schedule">Schedule</Option>
            </Select>
            {scheduleType === 'schedule' && (
                <div className="space-y-2 pl-1 pt-3">
                    <Button onClick={setPublishNow} type="link" className="p-0 h-auto mb-2">Switch to Publish Now</Button>
                    <Divider className="my-1"/>
                    {scheduleDates.map((item, index) => (
                        <div key={index}>
                            <div className="flex items-center gap-2">
                                <DatePicker value={item.date} onChange={(val) => updateSchedule(index, 'date', val)} className="flex-grow"/>
                                <TimePicker value={item.time} onChange={(val) => updateSchedule(index, 'time', val)} format="h:mm A" use12Hours className="flex-grow"/>
                                <Button shape="circle" icon={<i className="fa-solid fa-copy"></i>} onClick={() => duplicateSchedule(index)} />
                                {scheduleDates.length > 1 && <Button shape="circle" danger icon={<i className="fa-solid fa-trash-can"></i>} onClick={() => removeSchedule(index)} />}
                            </div>
                            {renderTimezoneInfo(item)}
                        </div>
                    ))}
                    <Button onClick={addSchedule} className="w-full mt-2" type="dashed">Add another date & time</Button>
                </div>
            )}
        </div>
    );
};

const YouTubeOptions = ({ youtubeProfile, youtubeOptions, setYoutubeOptions }) => {
    const [playlists, setPlaylists] = useState([]);
    const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
    const [isCreatePlaylistModalVisible, setCreatePlaylistModalVisible] = useState(false);
    const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
    const [newPlaylistDesc, setNewPlaylistDesc] = useState('');
    const [newPlaylistPrivacy, setNewPlaylistPrivacy] = useState('public');
    const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);

    const youtubeCategories = [
        { value: "1", label: "Film & Animation" }, { value: "2", label: "Autos & Vehicles" },
        { value: "10", label: "Music" }, { value: "15", label: "Pets & Animals" },
        { value: "17", label: "Sports" }, { value: "19", label: "Travel & Events" },
        { value: "20", label: "Gaming" }, { value: "22", label: "People & Blogs" },
        { value: "23", label: "Comedy" }, { value: "24", label: "Entertainment" },
        { value: "25", label: "News & Politics" }, { value: "26", label: "Howto & Style" },
        { value: "27", label: "Education" }, { value: "28", label: "Science & Technology" },
        { value: "29", label: "Nonprofits & Activism" }
    ];

    const fetchPlaylists = useCallback(async () => {
        if (!youtubeProfile) return;
        setIsLoadingPlaylists(true);
        try {
            const response = await axiosServices.post('/youtube/playlists', { channelId: youtubeProfile.platform_id });
            setPlaylists(response.data || []);
        } catch (error) {
            message.error("Could not fetch YouTube playlists for this channel.");
        } finally {
            setIsLoadingPlaylists(false);
        }
    }, [youtubeProfile]);
    
    useEffect(() => {
        fetchPlaylists();
    }, [fetchPlaylists]);

    const handleCreatePlaylist = async () => {
        setIsCreatingPlaylist(true);
        try {
            const res = await createPlaylistApi({
                channelId: youtubeProfile.platform_id,
                title: newPlaylistTitle,
                description: newPlaylistDesc,
                privacyStatus: newPlaylistPrivacy
            });
            if (!res.error) {
                message.success(`Playlist "${res.data.snippet.title}" created!`);
                const newPlaylist = res.data;
                setPlaylists(prev => [newPlaylist, ...prev]);
                handleOptionChange('playlistId', newPlaylist.id);
                setCreatePlaylistModalVisible(false);
                setNewPlaylistTitle('');
                setNewPlaylistDesc('');
                setNewPlaylistPrivacy('public');
            } else {
                message.error(res.message);
            }
        } catch (err) {
            message.error("Failed to create playlist.");
        } finally {
            setIsCreatingPlaylist(false);
        }
    };
    
    const handleOptionChange = (key, value) => {
        setYoutubeOptions(prev => ({
            ...prev,
            [youtubeProfile.platform_id]: {
                ...(prev[youtubeProfile.platform_id] || {}),
                [key]: value
            }
        }));
    };
    
    const channelOptions = youtubeOptions[youtubeProfile.platform_id] || {};

    return (
        <>
            <PlatformOption title={`YouTube Options: ${youtubeProfile.profile}`}>
                <div className="space-y-4">
                    <Input 
                        placeholder="Video Title (Required for YouTube)" 
                        value={channelOptions.title || ''} 
                        onChange={e => handleOptionChange('title', e.target.value)} 
                    />
                    <Select
                        mode="tags"
                        placeholder="Video Tags (e.g., marketing, tech)"
                        value={channelOptions.tags ? channelOptions.tags.split(',').map(t => t.trim()).filter(Boolean) : []}
                        onChange={(newTags) => handleOptionChange('tags', newTags.join(','))}
                        style={{ width: '100%' }}
                        tokenSeparators={[',']}
                    />
                    <div className="grid grid-cols-1 gap-4"> 
                        <Select placeholder="Category" value={channelOptions.categoryId} onChange={(value) => handleOptionChange('categoryId', value)}>
                            {youtubeCategories.map(cat => <Option key={cat.value} value={cat.value}>{cat.label}</Option>)}
                        </Select>
                    </div>
                    <Select
                        placeholder="Add to Playlist (optional)"
                        loading={isLoadingPlaylists}
                        value={channelOptions.playlistId}
                        onChange={(value) => handleOptionChange('playlistId', value)}
                        allowClear
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <Button type="link" onClick={() => setCreatePlaylistModalVisible(true)} block>
                                    <i className="fa-solid fa-plus mr-2"></i> Create New Playlist
                                </Button>
                            </>
                        )}
                    >
                        {playlists.map(p => <Option key={p.id} value={p.id}>{p.snippet.title}</Option>)}
                    </Select>

                    <div className="space-y-2 pt-2">
                        <label className="font-semibold text-gray-700 text-sm">Audience</label>
                        <Select placeholder="Is this video made for kids?" value={channelOptions.madeForKids} onChange={(value) => handleOptionChange('madeForKids', value)}>
                            <Option value={true}>Yes, it's made for kids</Option>
                            <Option value={false}>No, it's not made for kids</Option>
                        </Select>
                        <Divider className="my-2" />
                        <Checkbox checked={channelOptions.allowEmbedding === undefined ? true : channelOptions.allowEmbedding} onChange={(e) => handleOptionChange('allowEmbedding', e.target.checked)}>Allow embedding</Checkbox>
                        <Checkbox checked={channelOptions.notifySubscribers === undefined ? true : channelOptions.notifySubscribers} onChange={(e) => handleOptionChange('notifySubscribers', e.target.checked)}>Publish to subscriptions feed and notify subscribers</Checkbox>
                    </div>
                </div>
            </PlatformOption>
            <Modal
                title={`Create New Playlist for ${youtubeProfile.profile}`}
                open={isCreatePlaylistModalVisible}
                onCancel={() => setCreatePlaylistModalVisible(false)}
                onOk={handleCreatePlaylist}
                confirmLoading={isCreatingPlaylist}
                okText="Create Playlist"
            >
                <div className="space-y-4 py-4">
                    <Input placeholder="Playlist Title (Required)" value={newPlaylistTitle} onChange={e => setNewPlaylistTitle(e.target.value)} />
                    <TextArea rows={3} placeholder="Description (optional)" value={newPlaylistDesc} onChange={e => setNewPlaylistDesc(e.target.value)} />
                    <Select placeholder="Privacy" value={newPlaylistPrivacy} onChange={setNewPlaylistPrivacy} className="w-full">
                        <Option value="public">Public</Option>
                        <Option value="private">Private</Option>
                        <Option value="unlisted">Unlisted</Option>
                    </Select>
                </div>
            </Modal>
        </>
    );
};